// src/data/articles/joke-structures.js
// All language versions of the "7 Joke Structures" article.
//
// Keyword strategy:
//   EN → "joke structures", "how to structure a joke", "comedy writing techniques", "setup and punchline", "rule of three jokes"
//   FR → "structure d'une blague", "amorce chute", "techniques d'humour", "règle des trois blague", "comment structurer une blague"
//   ES → "estructura de un chiste", "planteo y remate", "técnicas de comedia", "regla de tres humor", "cómo estructurar un chiste"
//   AR → "بنية النكتة", "هيكل النكتة المضحكة", "المقدمة والضربة", "قاعدة الثلاثة في الفكاهة", "تقنيات كتابة النكات"

export default [
  // ── English ───────────────────────────────────────────────────────────────
  {
    slug: 'joke-structures',
    lang: 'en',
    translations: { en: 'joke-structures', fr: 'structures-de-blague', es: 'estructuras-de-chiste', ar: 'haykal-an-nukta' },
    title: '7 Joke Structures That Make Any Joke Funnier (Complete Guide)',
    seoTitle: '7 Joke Structures That Make Any Joke Funnier',
    seoDescription:
      'Discover the 7 most effective joke structures used in stand-up comedy and viral humor. Learn setup-punchline, misdirection, rule of three, and more comedy writing techniques.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'school', 'friends'],
    relatedArticles: ['how-to-write-a-funny-joke', 'benefits-of-laughter', 'what-joke-would-make-a-child-laugh'],
    intro: [
      'If you want to write better jokes, the first thing you need to understand is joke structure.',
      'Great humor isn\'t random. Most successful jokes follow specific structural patterns that trigger surprise, tension, and release. Once you understand how to structure a joke properly, you can turn ordinary ideas into genuinely funny material.',
      'In this guide, you\'ll discover the 7 most effective joke writing structures used in stand-up comedy, viral content, and professional comedy writing.',
    ],
    sections: [
      {
        number: 1,
        heading: 'The Classic Setup and Punchline Structure',
        anchor: 'classic-setup-punchline',
        content: [
          { type: 'paragraph', text: 'This is the foundation of joke writing structure.' },
          { type: 'lead', text: 'Setup' },
          { type: 'paragraph', text: 'Introduces a normal situation and creates an expectation.' },
          { type: 'lead', text: 'Punchline' },
          { type: 'paragraph', text: 'Delivers an unexpected twist.' },
          { type: 'lead', text: 'Example:' },
          { type: 'list', items: ['I started a new fitness routine last week.', 'So far, I\'ve trained my excuses.'] },
          { type: 'lead', text: 'Why it works:' },
          { type: 'paragraph', text: 'The punchline breaks the logical expectation built in the setup. This simple formula is the most universal joke structure.' },
        ],
      },
      {
        number: 2,
        heading: 'The Incongruity Structure',
        anchor: 'incongruity-structure',
        content: [
          { type: 'paragraph', text: 'This structure relies on contrast between expectation and reality.' },
          { type: 'paragraph', text: 'The brain naturally predicts outcomes. When that prediction is disrupted, laughter is triggered.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'I love long romantic walks… to the fridge.' },
          { type: 'paragraph', text: 'The sudden shift creates cognitive surprise. Incongruity is one of the core comedy writing techniques used by professionals.' },
        ],
      },
      {
        number: 3,
        heading: 'The Exaggeration Structure',
        anchor: 'exaggeration-structure',
        content: [
          { type: 'paragraph', text: 'Exaggeration amplifies reality until it becomes absurd.' },
          { type: 'paragraph', text: 'It works because it stretches truth just enough to remain relatable while being ridiculous.' },
          { type: 'lead', text: 'Example:' },
          { type: 'list', items: ['I sent a message at 8:02.', 'It\'s 8:04 and they haven\'t replied.', 'I\'ve already planned our emotional breakup.'] },
          { type: 'paragraph', text: 'This joke formula performs extremely well in social media humor.' },
        ],
      },
      {
        number: 4,
        heading: 'The Misdirection Structure',
        anchor: 'misdirection-structure',
        content: [
          { type: 'paragraph', text: 'Misdirection is essential if you want to learn how to write a punchline effectively.' },
          { type: 'paragraph', text: 'You guide the audience toward one logical ending… then flip it.' },
          { type: 'lead', text: 'Example:' },
          { type: 'list', items: ['I told my boss three companies were after me.', 'He asked which ones.', 'Gas, electric, and internet.'] },
          { type: 'paragraph', text: 'The final shift creates the laugh.' },
        ],
      },
      {
        number: 5,
        heading: 'The Rule of Three Structure',
        anchor: 'rule-of-three',
        content: [
          { type: 'paragraph', text: 'The brain loves patterns.' },
          { type: 'paragraph', text: 'In this joke writing structure, you list two normal items, then break the pattern with a funny third element.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'I want to be healthy, wealthy, and asleep by 9 PM.' },
          { type: 'paragraph', text: 'The third element disrupts the pattern and creates humor. The rule of three is widely used in stand-up comedy and speech writing.' },
        ],
      },
      {
        number: 6,
        heading: 'The Wordplay Structure',
        anchor: 'wordplay-structure',
        content: [
          { type: 'paragraph', text: 'Wordplay relies on double meanings and linguistic tricks.' },
          { type: 'paragraph', text: 'It forces the brain to reinterpret language quickly.' },
          { type: 'lead', text: 'Example:' },
          { type: 'paragraph', text: 'I used to be a baker, but I couldn\'t make enough dough.' },
          { type: 'paragraph', text: 'This joke structure works particularly well in short-form written humor.' },
        ],
      },
      {
        number: 7,
        heading: 'The Short Narrative Twist Structure',
        anchor: 'narrative-twist',
        content: [
          { type: 'paragraph', text: 'This is a mini-story ending with a strong twist.' },
          { type: 'paragraph', text: 'It\'s longer than a one-liner but builds more tension.' },
          { type: 'lead', text: 'Example structure:' },
          { type: 'list', items: ['Introduce a character', 'Present a problem', 'Deliver an unexpected resolution'] },
          { type: 'paragraph', text: 'The twist reframes the entire story in a surprising way. This structure is common in viral jokes and storytelling humor.' },
        ],
      },
      {
        number: 8,
        heading: 'Why Understanding Joke Structure Matters',
        anchor: 'why-structure-matters',
        content: [
          { type: 'paragraph', text: 'If you struggle with how to structure a joke, your humor may feel random or inconsistent.' },
          { type: 'lead', text: 'Structure helps you:' },
          { type: 'list', items: ['Control timing', 'Strengthen punchlines', 'Increase surprise', 'Avoid predictable endings', 'Improve audience reactions'] },
          { type: 'paragraph', text: 'Comedy isn\'t magic. It\'s mechanics.' },
        ],
      },
      {
        number: 9,
        heading: 'Common Joke Writing Mistakes',
        anchor: 'common-mistakes',
        content: [
          { type: 'paragraph', text: 'Even with a good joke formula, humor can fail if:' },
          { type: 'list', items: ['The setup is too long', 'The punchline is predictable', 'The joke is overexplained', 'There is no clear expectation to break'] },
          { type: 'paragraph', text: 'Strong joke writing structure keeps everything tight and focused.' },
        ],
      },
    ],
    tips: {
      heading: 'How to Practice Joke Writing Structure',
      intro: 'To improve your joke writing skills:',
      items: [
        'Write a simple everyday observation',
        'Choose one structure (exaggeration, misdirection, etc.)',
        'Build a setup',
        'Add an unexpected punchline',
        'Remove unnecessary words',
      ],
      outro: 'The clearer the structure, the stronger the joke.',
    },
    faq: [
      {
        q: 'What is the most common joke structure?',
        a: 'The setup and punchline structure is the most widely used in stand-up comedy, written humor, and everyday conversation.',
      },
      {
        q: 'Can you combine joke structures?',
        a: 'Yes. Many great jokes mix exaggeration with misdirection, or combine the rule of three with wordplay for stronger impact.',
      },
      {
        q: 'Is joke writing a talent or a skill?',
        a: 'Mostly a skill. Understanding structure makes a huge difference and can be learned through consistent practice.',
      },
      {
        q: 'How do I write a stronger punchline?',
        a: 'Make it unexpected, concise, and different from what the audience predicts. The more it subverts the setup, the stronger it lands.',
      },
    ],
    conclusion: [
      'Mastering joke structure is one of the fastest ways to improve your humor.',
      'Whether you use setup and punchline, exaggeration, misdirection, or the rule of three, structure gives your joke clarity and impact.',
      'Once you understand the mechanics behind comedy writing techniques, you can turn almost any idea into something funny.',
    ],
  },

  // ── French ────────────────────────────────────────────────────────────────
  // Keywords: "structure d'une blague", "amorce chute", "techniques d'humour",
  //           "règle des trois blague", "comment structurer une blague drôle"
  {
    slug: 'structures-de-blague',
    lang: 'fr',
    translations: { en: 'joke-structures', fr: 'structures-de-blague', es: 'estructuras-de-chiste', ar: 'haykal-an-nukta' },
    title: '7 Structures de Blague pour Faire Rire à Coup Sûr (Guide Complet)',
    seoTitle: '7 Structures de Blague pour Faire Rire à Coup Sûr',
    seoDescription:
      'Maîtrisez les 7 structures de blague qui font vraiment rire. Amorce-chute, règle des trois, exagération, misdirection : les techniques des humoristes professionnels expliquées simplement.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'school', 'friends'],
    relatedArticles: ['comment-ecrire-une-blague', 'bienfaits-du-rire', 'quelle-blague-fait-rire-les-enfants'],
    intro: [
      'Vous voulez écrire des blagues qui font vraiment rire ? La première chose à comprendre, c\'est la structure de la blague.',
      'Une bonne blague n\'est pas le fruit du hasard. Les meilleures d\'entre elles suivent des schémas précis qui jouent sur la surprise, la tension et la libération. Une fois que vous maîtrisez comment structurer une blague, vous pouvez transformer les situations les plus ordinaires en humour percutant.',
      'Dans ce guide, découvrez les 7 structures de blague les plus efficaces, utilisées dans le stand-up, les contenus viraux et la comédie professionnelle.',
    ],
    sections: [
      {
        number: 1,
        heading: 'La Structure Amorce-Chute (Setup-Punchline)',
        anchor: 'amorce-chute-classique',
        content: [
          { type: 'paragraph', text: 'C\'est la base de toute structure comique. Elle s\'applique à la quasi-totalité des blagues réussies.' },
          { type: 'lead', text: 'L\'amorce' },
          { type: 'paragraph', text: 'Elle présente une situation ordinaire et installe une attente chez l\'audience.' },
          { type: 'lead', text: 'La chute' },
          { type: 'paragraph', text: 'Elle délivre un retournement inattendu qui brise l\'attente.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'list', items: ['J\'ai commencé un nouveau programme sportif la semaine dernière.', 'Pour l\'instant, j\'ai surtout entraîné mes excuses.'] },
          { type: 'lead', text: 'Pourquoi ça marche :' },
          { type: 'paragraph', text: 'La chute brise la logique installée par l\'amorce. C\'est la structure de blague la plus universelle et la plus facile à apprendre.' },
        ],
      },
      {
        number: 2,
        heading: 'La Structure de l\'Incongruité',
        anchor: 'structure-incongruite',
        content: [
          { type: 'paragraph', text: 'Cette structure repose sur le contraste entre ce que le cerveau attend et ce qu\'il reçoit.' },
          { type: 'paragraph', text: 'Le cerveau anticipe constamment les suites logiques. Quand cette anticipation est déjouée, le rire surgit. C\'est ce que les chercheurs appellent la théorie de l\'incongruité.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: 'J\'adore les longues promenades romantiques… jusqu\'au réfrigérateur.' },
          { type: 'paragraph', text: 'Le glissement soudain crée une surprise cognitive immédiate. L\'incongruité est l\'une des techniques d\'humour les plus utilisées par les comédiens professionnels.' },
        ],
      },
      {
        number: 3,
        heading: 'La Structure de l\'Exagération',
        anchor: 'structure-exageration',
        content: [
          { type: 'paragraph', text: 'L\'exagération amplifie la réalité jusqu\'à l\'absurde. Elle étire la vérité juste assez pour rester reconnaissable, tout en devenant ridicule.' },
          { type: 'paragraph', text: 'Cette structure de blague est particulièrement efficace sur les réseaux sociaux, où les situations du quotidien exagérées génèrent un fort engagement.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'list', items: ['J\'ai envoyé un message à 8h02.', 'Il est 8h04 et personne n\'a répondu.', 'J\'ai déjà planifié notre rupture émotionnelle.'] },
          { type: 'paragraph', text: 'L\'absurdité de la situation la rend à la fois drôle et terriblement reconnaissable.' },
        ],
      },
      {
        number: 4,
        heading: 'La Structure du Détournement (Misdirection)',
        anchor: 'structure-detournement',
        content: [
          { type: 'paragraph', text: 'Le détournement est indispensable pour écrire une chute percutante. Vous guidez l\'audience vers une conclusion logique… puis vous la retournez.' },
          { type: 'paragraph', text: 'Plus le public est convaincu d\'avoir deviné la chute, plus l\'effet de surprise est puissant.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'list', items: ['J\'ai dit à mon chef que trois entreprises me voulaient.', 'Il m\'a demandé lesquelles.', 'Le gaz, l\'électricité et internet.'] },
          { type: 'paragraph', text: 'Le glissement final produit l\'effet comique.' },
        ],
      },
      {
        number: 5,
        heading: 'La Règle des Trois',
        anchor: 'regle-des-trois',
        content: [
          { type: 'paragraph', text: 'Le cerveau aime les séries. Quand vous en proposez deux éléments normaux, il anticipe un troisième du même type.' },
          { type: 'paragraph', text: 'La règle des trois consiste à briser ce schéma avec un troisième élément inattendu. C\'est l\'une des structures comiques les plus utilisées dans le stand-up et les discours humoristiques.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: 'Je veux être en bonne santé, riche et endormi à 21h.' },
          { type: 'paragraph', text: 'Le troisième élément rompt le rythme et crée le rire.' },
        ],
      },
      {
        number: 6,
        heading: 'La Structure du Jeu de Mots',
        anchor: 'structure-jeu-de-mots',
        content: [
          { type: 'paragraph', text: 'Le jeu de mots exploite les doubles sens, les calembours et les homophones pour créer une surprise linguistique.' },
          { type: 'paragraph', text: 'Il force le cerveau à retraiter le même mot avec deux significations différentes — ce décalage produit le rire.' },
          { type: 'lead', text: 'Exemple :' },
          { type: 'paragraph', text: 'J\'ai arrêté de faire du pain — je ne gagnais pas assez de blé.' },
          { type: 'paragraph', text: 'Le double sens du mot « blé » (la céréale et l\'argent en argot) crée la surprise. Cette structure de blague fonctionne particulièrement bien à l\'écrit.' },
        ],
      },
      {
        number: 7,
        heading: 'La Structure Narrative avec Chute',
        anchor: 'structure-narrative',
        content: [
          { type: 'paragraph', text: 'Cette structure est une mini-histoire qui se termine par un retournement fort. Plus longue qu\'un simple one-liner, elle construit une tension narrative avant de la libérer.' },
          { type: 'paragraph', text: 'Elle est particulièrement efficace dans les histoires virales sur les réseaux sociaux.' },
          { type: 'lead', text: 'Schéma type :' },
          { type: 'list', items: ['Présentez un personnage ou une situation', 'Développez un problème ou une attente', 'Livrez une résolution complètement inattendue'] },
          { type: 'paragraph', text: 'La chute finale recontextualise toute l\'histoire de façon surprenante.' },
        ],
      },
      {
        number: 8,
        heading: 'Pourquoi Maîtriser la Structure d\'une Blague ?',
        anchor: 'pourquoi-la-structure',
        content: [
          { type: 'paragraph', text: 'Si votre humour semble aléatoire ou ne fait pas rire, c\'est souvent un problème de structure.' },
          { type: 'lead', text: 'La structure vous permet de :' },
          { type: 'list', items: ['Contrôler le rythme', 'Renforcer les chutes', 'Maximiser la surprise', 'Éviter les fins prévisibles', 'Améliorer les réactions du public'] },
          { type: 'paragraph', text: 'L\'humour n\'est pas de la magie. C\'est de la mécanique.' },
        ],
      },
      {
        number: 9,
        heading: 'Les Erreurs à Éviter dans la Structure d\'une Blague',
        anchor: 'erreurs-structure',
        content: [
          { type: 'paragraph', text: 'Même avec une bonne formule, une blague peut échouer si :' },
          { type: 'list', items: ['L\'amorce est trop longue', 'La chute est prévisible', 'La blague est surexpliquée', 'Il n\'y a pas d\'attente claire à briser'] },
          { type: 'paragraph', text: 'Une structure de blague solide garde tout compact et percutant.' },
        ],
      },
    ],
    tips: {
      heading: 'Comment s\'Entraîner aux Structures de Blague',
      intro: 'Pour progresser rapidement :',
      items: [
        'Notez une observation simple du quotidien',
        'Choisissez une structure (exagération, détournement…)',
        'Construisez l\'amorce',
        'Ajoutez une chute inattendue',
        'Supprimez tous les mots inutiles',
      ],
      outro: 'Plus la structure est claire, plus la blague est percutante.',
    },
    faq: [
      {
        q: 'Quelle est la structure de blague la plus courante ?',
        a: 'La structure amorce-chute est la plus répandue dans le stand-up, l\'humour écrit et la conversation quotidienne.',
      },
      {
        q: 'Peut-on combiner plusieurs structures de blague ?',
        a: 'Oui. Les meilleures blagues mélangent souvent exagération et détournement, ou règle des trois et jeu de mots.',
      },
      {
        q: 'L\'humour est-il un talent ou une compétence ?',
        a: 'Avant tout une compétence. Comprendre les structures comiques fait une différence considérable et s\'apprend avec la pratique.',
      },
      {
        q: 'Comment écrire une chute plus forte ?',
        a: 'Elle doit être inattendue, concise et différente de ce que l\'audience prédit. Plus elle contredit l\'amorce, plus elle porte.',
      },
    ],
    conclusion: [
      'Maîtriser la structure d\'une blague est l\'un des moyens les plus rapides d\'améliorer son humour.',
      'Que vous utilisiez l\'amorce-chute, l\'exagération, le détournement ou la règle des trois, la structure donne à votre blague clarté et impact.',
      'Une fois que vous comprenez la mécanique derrière les techniques d\'humour, vous pouvez rendre presque n\'importe quelle idée drôle.',
    ],
  },

  // ── Spanish ───────────────────────────────────────────────────────────────
  // Keywords: "estructura de un chiste", "planteo y remate", "técnicas de comedia",
  //           "regla de tres humor", "cómo estructurar un chiste gracioso"
  {
    slug: 'estructuras-de-chiste',
    lang: 'es',
    translations: { en: 'joke-structures', fr: 'structures-de-blague', es: 'estructuras-de-chiste', ar: 'haykal-an-nukta' },
    title: '7 Estructuras de Chiste que Hacen Reír a Cualquiera (Guía Completa)',
    seoTitle: '7 Estructuras de Chiste que Hacen Reír a Cualquiera',
    seoDescription:
      'Domina las 7 estructuras de chiste más efectivas. Planteo-remate, regla de tres, exageración y misdirección: técnicas usadas por los mejores comediantes explicadas paso a paso.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'school', 'friends'],
    relatedArticles: ['como-escribir-un-chiste', 'beneficios-de-la-risa', 'que-chiste-hace-reir-a-un-nino'],
    intro: [
      'Si quieres escribir mejores chistes, lo primero que necesitas entender es la estructura del chiste.',
      'El buen humor no es aleatorio. La mayoría de los chistes exitosos siguen patrones estructurales específicos que generan sorpresa, tensión y liberación. Una vez que entiendes cómo estructurar un chiste correctamente, puedes convertir ideas ordinarias en material genuinamente gracioso.',
      'En esta guía descubrirás las 7 estructuras de chiste más efectivas usadas en el stand-up, el contenido viral y la escritura profesional de comedia.',
    ],
    sections: [
      {
        number: 1,
        heading: 'La Estructura Clásica Planteo-Remate (Setup-Punchline)',
        anchor: 'planteo-remate-clasico',
        content: [
          { type: 'paragraph', text: 'Esta es la base de toda estructura de chiste. Se aplica en la gran mayoría de los chistes exitosos.' },
          { type: 'lead', text: 'Planteo' },
          { type: 'paragraph', text: 'Presenta una situación normal y construye una expectativa en el público.' },
          { type: 'lead', text: 'Remate' },
          { type: 'paragraph', text: 'Entrega el giro inesperado que rompe esa expectativa.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'list', items: ['Empecé una nueva rutina de ejercicios la semana pasada.', 'Hasta ahora, solo he entrenado mis excusas.'] },
          { type: 'lead', text: 'Por qué funciona:' },
          { type: 'paragraph', text: 'El remate rompe la lógica instalada por el planteo. Esta fórmula es la estructura de chiste más universal.' },
        ],
      },
      {
        number: 2,
        heading: 'La Estructura de la Incongruencia',
        anchor: 'estructura-incongruencia',
        content: [
          { type: 'paragraph', text: 'Esta estructura se basa en el contraste entre lo que el cerebro espera y lo que realmente recibe.' },
          { type: 'paragraph', text: 'El cerebro predice resultados constantemente. Cuando esa predicción falla, se produce la risa. Es lo que los investigadores llaman la teoría de la incongruencia del humor.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Me encantan los largos paseos románticos… hasta la nevera.' },
          { type: 'paragraph', text: 'El giro inesperado crea una sorpresa cognitiva inmediata. La incongruencia es una de las técnicas de comedia más usadas por los profesionales.' },
        ],
      },
      {
        number: 3,
        heading: 'La Estructura de la Exageración',
        anchor: 'estructura-exageracion',
        content: [
          { type: 'paragraph', text: 'La exageración amplifica la realidad hasta lo absurdo. Funciona porque estira la verdad lo suficiente para seguir siendo identificable, pero llevándola al ridículo.' },
          { type: 'paragraph', text: 'Esta estructura de chiste funciona especialmente bien en el humor de redes sociales, donde las situaciones cotidianas exageradas generan gran engagement.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'list', items: ['Envié un mensaje a las 8:02.', 'Son las 8:04 y no ha respondido.', 'Ya he planificado nuestra ruptura emocional.'] },
          { type: 'paragraph', text: 'El absurdo de la situación la hace graciosa y reconocible al mismo tiempo.' },
        ],
      },
      {
        number: 4,
        heading: 'La Estructura de la Misdirección',
        anchor: 'estructura-misdirección',
        content: [
          { type: 'paragraph', text: 'La misdirección es imprescindible si quieres aprender a escribir un remate fuerte. Llevas al público hacia un final lógico… y luego lo giras.' },
          { type: 'paragraph', text: 'Cuanto más convencido está el público de haber adivinado el final, mayor es el efecto cómico del giro.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'list', items: ['Le dije a mi jefe que tres empresas me querían.', 'Me preguntó cuáles.', 'El gas, la luz e internet.'] },
          { type: 'paragraph', text: 'El cambio final produce la carcajada.' },
        ],
      },
      {
        number: 5,
        heading: 'La Regla de Tres',
        anchor: 'regla-de-tres',
        content: [
          { type: 'paragraph', text: 'Al cerebro le encantan los patrones. Cuando ves dos elementos de una serie, anticipas automáticamente un tercero del mismo tipo.' },
          { type: 'paragraph', text: 'La regla de tres en la estructura del chiste consiste en presentar dos elementos normales y romper el patrón con un tercer elemento inesperado. Es una de las técnicas de comedia más usadas en el stand-up y los discursos humorísticos.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Quiero ser sano, rico y dormido a las 9 de la noche.' },
          { type: 'paragraph', text: 'El tercer elemento rompe el ritmo y genera la risa.' },
        ],
      },
      {
        number: 6,
        heading: 'La Estructura del Juego de Palabras',
        anchor: 'estructura-juego-palabras',
        content: [
          { type: 'paragraph', text: 'El juego de palabras explota los dobles sentidos, los calambures y las expresiones literales para crear una sorpresa lingüística.' },
          { type: 'paragraph', text: 'Obliga al cerebro a reinterpretar la misma palabra con dos significados diferentes, y ese desfase genera la risa.' },
          { type: 'lead', text: 'Ejemplo:' },
          { type: 'paragraph', text: 'Era panadero, pero no me levantaba el pan.' },
          { type: 'paragraph', text: 'El doble sentido (el pan que no sube y el dinero que no alcanza) crea la sorpresa. Esta estructura funciona especialmente bien en el humor escrito.' },
        ],
      },
      {
        number: 7,
        heading: 'La Estructura Narrativa con Giro',
        anchor: 'estructura-narrativa-giro',
        content: [
          { type: 'paragraph', text: 'Esta estructura es una mini-historia que termina con un giro fuerte. Es más larga que un one-liner, pero construye más tensión narrativa antes de liberarla.' },
          { type: 'paragraph', text: 'Es una de las estructuras de chiste más frecuentes en los contenidos virales de humor y el storytelling cómico.' },
          { type: 'lead', text: 'Esquema tipo:' },
          { type: 'list', items: ['Presenta un personaje o situación', 'Desarrolla un problema o expectativa', 'Entrega una resolución completamente inesperada'] },
          { type: 'paragraph', text: 'El giro final recontextualiza toda la historia de forma sorprendente.' },
        ],
      },
      {
        number: 8,
        heading: 'Por Qué Importa Entender la Estructura del Chiste',
        anchor: 'por-que-importa-la-estructura',
        content: [
          { type: 'paragraph', text: 'Si tu humor parece aleatorio o no funciona, el problema casi siempre es la estructura.' },
          { type: 'lead', text: 'La estructura te permite:' },
          { type: 'list', items: ['Controlar el ritmo', 'Fortalecer los remates', 'Maximizar la sorpresa', 'Evitar finales predecibles', 'Mejorar la reacción del público'] },
          { type: 'paragraph', text: 'El humor no es magia. Es mecánica.' },
        ],
      },
      {
        number: 9,
        heading: 'Errores Frecuentes en la Estructura del Chiste',
        anchor: 'errores-estructura-chiste',
        content: [
          { type: 'paragraph', text: 'Incluso con una buena fórmula, un chiste puede fallar si:' },
          { type: 'list', items: ['El planteo es demasiado largo', 'El remate es predecible', 'El chiste está sobreexplicado', 'No hay una expectativa clara que romper'] },
          { type: 'paragraph', text: 'Una estructura de chiste sólida mantiene todo compacto y efectivo.' },
        ],
      },
    ],
    tips: {
      heading: 'Cómo Practicar la Estructura del Chiste',
      intro: 'Para mejorar tus habilidades de escritura cómica:',
      items: [
        'Escribe una observación simple del día a día',
        'Elige una estructura (exageración, misdirección…)',
        'Construye el planteo',
        'Añade un remate inesperado',
        'Elimina las palabras innecesarias',
      ],
      outro: 'Cuanto más clara sea la estructura, más fuerte será el chiste.',
    },
    faq: [
      {
        q: '¿Cuál es la estructura de chiste más común?',
        a: 'La estructura planteo-remate es la más usada en el stand-up, el humor escrito y la conversación cotidiana.',
      },
      {
        q: '¿Se pueden combinar varias estructuras de chiste?',
        a: 'Sí. Los mejores chistes suelen mezclar exageración con misdirección, o combinar la regla de tres con juego de palabras.',
      },
      {
        q: '¿Escribir chistes es un talento o una habilidad?',
        a: 'Principalmente una habilidad. Entender la estructura marca una gran diferencia y se aprende con práctica constante.',
      },
      {
        q: '¿Cómo escribo un remate más fuerte?',
        a: 'Que sea inesperado, conciso y diferente a lo que el público predice. Cuanto más contradiga el planteo, más impacto tendrá.',
      },
    ],
    conclusion: [
      'Dominar la estructura del chiste es una de las formas más rápidas de mejorar tu humor.',
      'Ya uses el planteo-remate, la exageración, la misdirección o la regla de tres, la estructura le da a tu chiste claridad e impacto.',
      'Una vez que entiendes la mecánica detrás de las técnicas de comedia, puedes convertir casi cualquier idea en algo gracioso.',
    ],
  },

  // ── Arabic ────────────────────────────────────────────────────────────────
  // Keywords: "بنية النكتة", "هيكل النكتة المضحكة", "المقدمة والضربة",
  //           "قاعدة الثلاثة في الفكاهة", "تقنيات كتابة النكات"
  {
    slug: 'haykal-an-nukta',
    lang: 'ar',
    translations: { en: 'joke-structures', fr: 'structures-de-blague', es: 'estructuras-de-chiste', ar: 'haykal-an-nukta' },
    title: '7 طرق لبناء النكتة تجعل أي موقف فكاهي أكثر إضحاكاً ',
    seoTitle: '7 طرق لبناء النكتة تجعل أي موقف فكاهي أكثر إضحاكاً ',
    seoDescription:
      'اكتشف أكثر ٧ أبنية للنكتة فاعليةً: المقدمة والضربة، قاعدة الثلاثة، المبالغة، والتضليل الأنيق. تقنيات الكوميديين المحترفين بأسلوب واضح وأمثلة عملية.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'school', 'friends'],
    relatedArticles: ['kaifa-taktub-nuktatan', 'fawaid-al-dahk', 'ay-nuktah-tudhik-al-tifl'],
    intro: [
      'إن أردتَ كتابة نكات تُضحك فعلاً، فأول شيء عليك إتقانه هو بنية النكتة.',
      'الفكاهة الجيدة ليست ضرباً من الحظ أو الموهبة الفطرية. معظم النكات الناجحة تتبع أنماطاً هيكلية محددة تُفجّر المفاجأة، وتبني التوتر، ثم تُحرّر الضحكة. حين تُتقن كيفية بناء النكتة، تستطيع تحويل أبسط المواقف اليومية إلى مادة فكاهية حقيقية.',
      'في هذا الدليل، ستكتشف أكثر ٧ أبنية للنكتة فاعليةً، كما يستخدمها كوميديو المسرح وصُنّاع المحتوى الفيروسي وكتّاب الكوميديا المحترفون.',
    ],
    sections: [
      {
        number: 1,
        heading: 'البنية الكلاسيكية: المقدمة والضربة',
        anchor: 'al-muqaddima-wal-darba',
        content: [
          { type: 'paragraph', text: 'هذه هي الأساس الذي تقوم عليه كل بنية نكتة ناجحة تقريباً.' },
          { type: 'lead', text: 'المقدمة' },
          { type: 'paragraph', text: 'تُقدّم موقفاً عادياً وتبني توقعاً في ذهن الجمهور.' },
          { type: 'lead', text: 'الضربة (الخاتمة)' },
          { type: 'paragraph', text: 'تُقدّم التحوّل غير المتوقع الذي يُحطّم ذلك التوقع.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'list', items: ['بدأتُ برنامجًا رياضيًا جديدًا الأسبوع الماضي.', 'حتى الآن، درّبتُ أعذاري فحسب.'] },
          { type: 'lead', text: 'لماذا ينجح هذا:' },
          { type: 'paragraph', text: 'الضربة تكسر المنطق الذي أرسته المقدمة. هذه الصيغة هي أكثر أبنية النكتة شيوعاً وأوسعها انتشاراً.' },
        ],
      },
      {
        number: 2,
        heading: 'بنية التناقض',
        anchor: 'bunyat-at-tanaqudd',
        content: [
          { type: 'paragraph', text: 'تقوم هذه البنية على التباين بين ما يتوقعه الدماغ وما يتلقّاه فعلاً.' },
          { type: 'paragraph', text: 'الدماغ يتنبأ باستمرار بالنتائج المنطقية. حين تُكذَّب هذه التنبؤات، تنفجر الضحكة. وهذا ما يُعرف في علم النفس بنظرية التناقض في الفكاهة.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'أحبّ التجوّل الرومانسي الطويل... حتى الثلاجة.' },
          { type: 'paragraph', text: 'التحوّل المفاجئ يصنع مفاجأة إدراكية فورية. التناقض هو من أعمق تقنيات الفكاهة التي يستخدمها المحترفون.' },
        ],
      },
      {
        number: 3,
        heading: 'بنية المبالغة',
        anchor: 'bunyat-al-mubalagha',
        content: [
          { type: 'paragraph', text: 'المبالغة تُضخّم الواقع حتى يبلغ حدّ السخف. وتنجح لأنها تمدّ الحقيقة بما يكفي لتبقى مألوفة، بينما تُصبح هزليةً في الوقت ذاته.' },
          { type: 'paragraph', text: 'تؤدي هذه البنية أداءً ممتازاً في الفكاهة على وسائل التواصل الاجتماعي، حيث تُولّد المواقف اليومية المبالغ فيها تفاعلاً عالياً.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'list', items: ['أرسلتُ رسالةً الساعة 8:02.', 'الساعة 8:04 ولم يردّ أحد.', 'وضعتُ بالفعل خطةً كاملةً لانفصالنا العاطفي.'] },
          { type: 'paragraph', text: 'سخافة الموقف تجعله مضحكاً ومألوفاً في آنٍ واحد.' },
        ],
      },
      {
        number: 4,
        heading: 'بنية التضليل',
        anchor: 'bunyat-at-tadlil',
        content: [
          { type: 'paragraph', text: 'التضليل الأنيق ضروري إن أردتَ كتابة ضربة خاتمة قوية. تقود الجمهور نحو نهايةٍ منطقية... ثم تقلبها رأساً على عقب.' },
          { type: 'paragraph', text: 'كلما كان الجمهور أكثر اقتناعاً باقتراب الخاتمة المتوقعة، كان تأثير الانقلاب أشدّ إضحاكاً.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'list', items: ['أخبرتُ مديري أن ثلاث جهات تسعى إليّ.', 'سألني: أيّ جهات؟', 'الغاز والكهرباء والإنترنت.'] },
          { type: 'paragraph', text: 'التحوّل النهائي هو ما يُحدث الضحكة.' },
        ],
      },
      {
        number: 5,
        heading: 'قاعدة الثلاثة',
        anchor: 'qaeidat-ath-thalatha',
        content: [
          { type: 'paragraph', text: 'يُحبّ الدماغ الأنماط. حين ترى عنصرين من سلسلة، تتوقع تلقائياً عنصراً ثالثاً من النوع نفسه.' },
          { type: 'paragraph', text: 'قاعدة الثلاثة في بنية النكتة تقوم على سرد عنصرين عاديين ثم كسر النمط بعنصر ثالث مفاجئ. وهي من أكثر أساليب الكوميديا استخداماً في المسرح وأحاديث الإلقاء.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'أريد أن أكون بصحة جيدة، وثرياً، ونائماً في التاسعة مساءً.' },
          { type: 'paragraph', text: 'العنصر الثالث يكسر الإيقاع ويُولّد الضحك.' },
        ],
      },
      {
        number: 6,
        heading: 'بنية التورية واللعب بالألفاظ',
        anchor: 'bunyat-at-tawriya',
        content: [
          { type: 'paragraph', text: 'التورية تستغلّ المعاني المزدوجة والجناس لتصنع مفاجأةً لغوية. وتُجبر الدماغ على إعادة تفسير الكلمة ذاتها بمعنيين مختلفين — وهذا الانتقال السريع يُولّد الضحكة.' },
          { type: 'paragraph', text: 'تزخر العربية بالجناس والتورية، مما يجعل هذه البنية أداةً قوية بشكل خاص في الفكاهة العربية المكتوبة.' },
          { type: 'lead', text: 'مثال:' },
          { type: 'paragraph', text: 'كنتُ أعمل خبّازاً، لكنني لم أكن أجني ما يكفي من العجين.' },
          { type: 'paragraph', text: 'الضحكة تنبع من المعنى المزدوج: عجين الخبز وعجين المال. هذه البنية تعمل جيداً بشكل خاص في الفكاهة المكتوبة والمنشورات.' },
        ],
      },
      {
        number: 7,
        heading: 'بنية الحكاية المقلوبة',
        anchor: 'bunyat-al-hikaya-al-maqlouba',
        content: [
          { type: 'paragraph', text: 'هذه البنية هي قصة قصيرة تنتهي بانقلاب مفاجئ. إنها أطول من النكتة العادية، لكنها تبني توتراً أعمق قبل تحريره.' },
          { type: 'paragraph', text: 'وهي من أكثر أبنية النكتة الفيروسية انتشاراً في محتوى الفكاهة على وسائل التواصل الاجتماعي.' },
          { type: 'lead', text: 'البنية النموذجية:' },
          { type: 'list', items: ['قدّم شخصية أو موقفاً', 'طوّر مشكلة أو توقعاً', 'أوصل إلى حلٍّ غير متوقع تماماً'] },
          { type: 'paragraph', text: 'النهاية المقلوبة تُعيد تأطير القصة بأسرها بطريقة مدهشة.' },
        ],
      },
      {
        number: 8,
        heading: 'لماذا تهمّ بنية النكتة؟',
        anchor: 'limadha-tahimmu-al-bunyah',
        content: [
          { type: 'paragraph', text: 'إن بدا فكاهتك عشوائياً أو لم يُضحك كما تتوقع، فالمشكلة في الغالب هي البنية.' },
          { type: 'lead', text: 'البنية السليمة تُمكّنك من:' },
          { type: 'list', items: ['التحكم في التوقيت', 'تقوية الضربة الخاتمة', 'تعظيم عنصر المفاجأة', 'تجنّب النهايات المتوقعة', 'تحسين تفاعل الجمهور'] },
          { type: 'paragraph', text: 'الفكاهة ليست سحراً. إنها ميكانيكا.' },
        ],
      },
      {
        number: 9,
        heading: 'أخطاء شائعة في بنية النكتة',
        anchor: 'akhtaa-fi-al-bunyah',
        content: [
          { type: 'paragraph', text: 'حتى مع امتلاك صيغة جيدة، قد تفشل النكتة إذا:' },
          { type: 'list', items: ['كانت المقدمة طويلة جداً', 'كانت الضربة متوقعة', 'كانت النكتة مُشرحة بالتفصيل', 'لم يكن هناك توقع واضح لكسره'] },
          { type: 'paragraph', text: 'بنية النكتة القوية تحافظ على كل شيء مضغوطاً ومؤثراً.' },
        ],
      },
    ],
    tips: {
      heading: 'كيف تتدرّب على أبنية النكتة',
      intro: 'لتطوير مهاراتك في كتابة النكات:',
      items: [
        'دوّن ملاحظةً بسيطة من يومك',
        'اختر بنيةً واحدة (مبالغة، تضليل...)',
        'أنشئ مقدمةً واضحة',
        'أضف ضربةً خاتمةً غير متوقعة',
        'احذف كل كلمة زائدة',
      ],
      outro: 'كلما كانت البنية أوضح، كانت النكتة أشدّ تأثيراً.',
    },
    faq: [
      {
        q: 'ما أكثر أبنية النكتة شيوعاً؟',
        a: 'بنية المقدمة والضربة هي الأكثر استخداماً في مسرح الكوميديا والفكاهة المكتوبة والحديث اليومي.',
      },
      {
        q: 'هل يمكن دمج أبنية النكتة المختلفة؟',
        a: 'نعم. كثير من أفضل النكات تجمع المبالغة مع التضليل، أو تُدمج قاعدة الثلاثة مع التورية لتأثير أقوى.',
      },
      {
        q: 'هل كتابة النكات موهبة أم مهارة؟',
        a: 'هي في معظمها مهارة. فهم البنية يُحدث فارقاً كبيراً ويمكن اكتسابها بالممارسة المنتظمة.',
      },
      {
        q: 'كيف أكتب ضربةً خاتمةً أقوى؟',
        a: 'اجعلها غير متوقعة وموجزة ومختلفة عما يتوقعه الجمهور. كلما ناقضت المقدمة بشكل أكبر، كان وقعها أشدّ.',
      },
    ],
    conclusion: [
      'إتقان بنية النكتة هو من أسرع الطرق لتطوير فكاهتك.',
      'سواء استخدمتَ المقدمة والضربة، أو المبالغة، أو التضليل، أو قاعدة الثلاثة — البنية تمنح نكتتك الوضوح والتأثير.',
      'حين تفهم الميكانيكا الكامنة وراء تقنيات كتابة النكات، تستطيع تحويل أي فكرة تقريباً إلى شيء مضحك.',
    ],
  },
];
